import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';

import { FjSideNav, FjSideNavItem } from 'components/primitives';
import { TOOL_BUTTON_NAMES } from 'constants/user-account';
import LicenseDetailsIcon from 'icons/license-details';
import { NavigationActions } from 'actions';
import UserIcon from 'icons/user';
import FjuIcon from 'icons/fju';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import UserAccountDrawer from '../user-account-drawer';

const AdminToolArea = (): React.JSX.Element => {
  const drawerOpen = useAppSelector((state) => state.navigation.leftDrawerOpen);
  const requestedTab = useAppSelector((state) => state.navigation.activeTab);
  const theme = useTheme();

  const dispatch = useAppDispatch();
  const openLeftDrawer = (): void => {
    dispatch(NavigationActions.openLeftDrawer());
  };
  const closeLeftDrawer = (): void => {
    dispatch(NavigationActions.closeLeftDrawer());
  };

  const [activeTab, setActiveTab] = useState('');

  // If a tab was requested via Redux action, activate it (class componentDidUpdate).
  // prevRef makes the initial run a no-op, matching componentDidUpdate.
  const prevRequestedTab = useRef(requestedTab);
  useEffect(() => {
    if (requestedTab && requestedTab !== prevRequestedTab.current) {
      setActiveTab(requestedTab);
    }
    prevRequestedTab.current = requestedTab;
  }, [requestedTab]);

  const sideNavStyle = {
    primaryColor: theme.colors.selected,
    backgroundColor: theme.colors.toolbar,
    width: theme.grid.units(4),
    titleFontSize: theme.fontSizes.sidebarTitle.default,
    titleHeight: '70px',
    iconBoxHeight: theme.grid.units(4),
    iconSize: '40px',
  };

  const handleTabChange = (tabId: string): void => {
    openLeftDrawer();
    setActiveTab(tabId);
  };

  const handleCloseDrawer = (): void => closeLeftDrawer();

  return (
    <React.Fragment>
      <FjSideNav
        activeTab={drawerOpen ? activeTab : ''}
        // Runtime object intentionally omits `colors`; cast to satisfy FjSideNav's
        // required SideNavTheme shape without altering the value passed at runtime.
        style={sideNavStyle as React.ComponentProps<typeof FjSideNav>['style']}
        onTabSelected={(id: string) => handleTabChange(id)}
      >
        <FjSideNavItem
          title="User"
          id={TOOL_BUTTON_NAMES.USER}
          icon={<UserIcon style={theme.colors.sideNavIcons} />}
          titleFontSizeOverride={theme.fontSizes.sidebarTitle.large}
          data-testid="user-drawer-button"
        />
        <FjSideNavItem
          title="License"
          id={TOOL_BUTTON_NAMES.LICENSE}
          icon={<LicenseDetailsIcon style={theme.colors.sideNavIcons} />}
          data-testid="license-drawer-button"
        />
        <FjSideNavItem
          title="FlowJo University"
          id={TOOL_BUTTON_NAMES.FJU}
          icon={<FjuIcon style={theme.colors.sideNavIcons} />}
          titleFontSizeOverride={theme.fontSizes.sidebarTitle.small}
          data-testid="fju-drawer-button"
        />
      </FjSideNav>
      <UserAccountDrawer
        open={drawerOpen}
        onCloseRequest={() => handleCloseDrawer()}
        activeTab={activeTab}
      />
    </React.Fragment>
  );
};

export default AdminToolArea;
